import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { List, Checkbox, Row, Col, Divider } from "antd";
import { GenericSearchableList, useResponsiveSettings, DataCard } from "@seims/shared";
import eventsService from "../../../services/eventsService";

export default function VenueList({ onSelect, newlyAddedVenue, resetCounter, selectedVenueId, disabled = false }) {
  const [venues, setVenues] = useState([]);
  const [venuesToDisplay, setVenuesToDisplay] = useState([]);
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [highlightedId, setHighlightedId] = useState(null);
  const { pageSize } = useResponsiveSettings();


  const itemRefs = useRef({});

  useEffect(() => {
    loadVenues();

    // Only clear if there's NO external selection
    if (!selectedVenueId) {
      setSelectedVenue(null);
    }

    setHighlightedId(null);
  }, [resetCounter]);

  useEffect(() => {
    if (newlyAddedVenue) {

      const updated = [newlyAddedVenue, ...venues.filter(v => v.id !== newlyAddedVenue.id)];

      // Force fresh dataset (this resets pagination internally)
      setVenues(updated);
      setVenuesToDisplay(updated);

      // select + highlight
      setSelectedVenue(newlyAddedVenue);

      setHighlightedId(newlyAddedVenue.id);

      setTimeout(() => {
        setHighlightedId(null);
      }, 2000);
    }
  }, [newlyAddedVenue]);

  useEffect(() => {
    if (selectedVenue?.id) {
      setTimeout(() => {
        const el = itemRefs.current[selectedVenue.id];
        if (el) {
          el.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 200);
    }
  }, [selectedVenue]);

  const loadVenues = async () => {
    const res = await eventsService.getEventVenues(5000);
    let results = res?.data?.results ?? [];

    // If editing and we have a selected venue ID
    if (selectedVenueId) {
      const selected = results.find(
        (v) => String(v.id) === String(selectedVenueId)
      );

      if (selected) {
        //  Move selected venue to top
        results = [
          selected,
          ...results.filter(
            (v) => String(v.id) !== String(selectedVenueId)
          ),
        ];
      }
    }

    setVenues(results);
    setVenuesToDisplay(results);
  };

  useEffect(() => {
    if (!selectedVenueId || venues.length === 0) return;

    const selected = venues.find(
      (v) => String(v.id) === String(selectedVenueId)
    );

    if (selected) {
      setSelectedVenue(selected);
    }
  }, [selectedVenueId, venues]);

  // SEARCH
  const handleSearch = (text) => {
    setCurrentPage(1);
    const value = text.toLowerCase();

    const filtered = venues.filter(
      (venue) =>
        venue.name?.toLowerCase().includes(value) ||
        venue?.type?.value?.toLowerCase().includes(value) ||
        venue?.address?.province?.toLowerCase().includes(value) ||
        venue?.address?.cityOrTown?.toLowerCase().includes(value)
    );

    setVenuesToDisplay(filtered);
  };

  useEffect(() => {
    handleSearch("");   // load initial list
  }, []);

  // SELECT + SCROLL
  const handleSelect = (venue) => {
    if (selectedVenue?.id === venue.id) {
      // Deselect
      setSelectedVenue(null);
      onSelect(null);
      return;
    }

    setSelectedVenue(venue);
    onSelect(venue);

    // Scroll into view
    setTimeout(() => {
      const el = itemRefs.current[venue.id];
      if (el) {
        el.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }, 100);
  };

  return (
    <>

      {selectedVenue && (
        <SelectedVenueCard disabled={disabled} selectedVenue={selectedVenue} handleCardToggle={handleSelect} />
      )}

      {(!disabled) && (
        <GenericSearchableList
          searchHint="Search for a venue"
          onHandleSearch={handleSearch}
          dataSource={venuesToDisplay}
          pagination={{
            current: currentPage,
            onChange: (page) => setCurrentPage(page),
          }}
          renderItem={(venue, index) => {
            const isSelected = Number(selectedVenueId) === Number(venue.id);
            const rowIndex = (currentPage - 1) * pageSize + index;

            // Transform your raw venue object into the UI schema
            const cardData = [
              { label: 'Name', value: venue.name },
              { label: 'Type', value: venue?.type?.value || 'N/A' },
              { label: 'Address', value: `${venue?.address?.cityOrTown}, ${venue?.address?.province}` }
            ];

            return (
              <List.Item >
                <DataCard
                  index={rowIndex}
                  isSelected={isSelected}
                  onToggleSelect={() => handleSelect(venue)}
                  details={cardData}
                />
              </List.Item>
            );
          }}
        />
      )}
    </>
  );
}

const SelectedVenueCard = ({ selectedVenue, handleCardToggle, disabled = false }) => {

  // 1. Transform the selected venue data into the generic array format
  const selectedCardData = [
    { label: 'Name', value: selectedVenue.name },
    { label: 'Type', value: selectedVenue?.type?.value ?? selectedVenue?.venueType?.value ?? 'N/A' },
    { label: 'Address', value: `${selectedVenue?.address?.cityOrTown}, ${selectedVenue?.address?.province}` }
  ];

  return (
    <div style={{ marginBottom: 16 }}>

      {/* 2. Render the highly reusable DataCard */}
      <DataCard
        disabled={disabled}
        cardName="Venue"
        isSelected={true}
        onToggleSelect={() => handleCardToggle(selectedVenue)}
        details={selectedCardData}
      />
      <Divider style={{ margin: "16px 0" }} />
    </div>
  );
}

SelectedVenueCard.propTypes = {
  selectedVenue: PropTypes.shape({
    id: PropTypes.any,
    name: PropTypes.string.isRequired,
    venueType: PropTypes.shape({
      value: PropTypes.string,
      key: PropTypes.number,
    }),
    type: PropTypes.shape({
      value: PropTypes.string,
      key: PropTypes.number,
    }),
    address: PropTypes.shape({
      cityOrTown: PropTypes.string,
      province: PropTypes.string,
    }),
  }),
  handleCardToggle: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};